import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { OtpPanel } from '@/customer/components/auth/otp-panel'
import { PhoneEntryPanel } from '@/customer/components/auth/phone-entry-panel'
import {
  VERIFICATION_SUCCESS_HOLD_MS,
  VerificationSuccessPanel,
} from '@/customer/components/auth/verification-success-panel'
import { PageContainer } from '@/customer/components/page-container'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { meQueryKey, useGetMe } from '@/customer/hooks/use-get-me'
import { authClient, isAuthError } from '@/customer/lib/auth'
import { routes } from '@/customer/routes'

type Step = 'entry' | 'otp' | 'success'

/**
 * Change-phone flow using better-auth's phone number plugin.
 * Verify-first: `phoneNumber.sendOtp` dispatches to the new number and
 * `phoneNumber.verify({ updatePhoneNumber: true })` performs the atomic
 * swap on success. `members.phone` stays untouched until verification
 * lands, so an abandoned attempt can never invalidate the account.
 */
export function PhoneUpdatePage() {
  const homeUrl = routes.account.index
  useDocumentTitle('Update your phone')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const me = useGetMe()

  const [step, setStep] = useState<Step>('entry')
  const [pendingPhone, setPendingPhone] = useState<string | null>(null)
  const [entryErrorCode, setEntryErrorCode] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleEntrySubmit(newPhone: string) {
    setIsSubmitting(true)
    setEntryErrorCode(null)
    const { data, error } = await authClient.phoneNumber.sendOtp({ phoneNumber: newPhone })
    setIsSubmitting(false)
    if (error) {
      setEntryErrorCode(isAuthError(error) ? error.code : 'UNKNOWN')
      return
    }
    if (!data) {
      setEntryErrorCode('UNKNOWN')
      return
    }
    setPendingPhone(newPhone)
    setStep('otp')
  }

  function goBackToEntry() {
    setPendingPhone(null)
    setEntryErrorCode(null)
    setStep('entry')
  }

  return (
    <PageContainer
      backLink={
        step === 'otp'
          ? { onClick: goBackToEntry, label: 'Use a different number' }
          : step === 'entry'
            ? { to: homeUrl, label: 'Back to account' }
            : undefined
      }
      headline={[
        { text: 'Update your', color: 'yellow' },
        { text: 'phone number', color: 'blue' },
      ]}
    >
      <AnimatePresence mode="wait">
        {step === 'success' ? (
          <motion.div key="success">
            <VerificationSuccessPanel active />
          </motion.div>
        ) : step === 'otp' && pendingPhone ? (
          <motion.div key="otp" exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            <PhoneChangeOtp
              pendingPhone={pendingPhone}
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: meQueryKey })
                setStep('success')
                window.setTimeout(() => navigate(homeUrl), VERIFICATION_SUCCESS_HOLD_MS)
              }}
            />
          </motion.div>
        ) : (
          <motion.div key="entry">
            <PhoneEntryPanel
              currentValue={me.data?.member.phone}
              errorCode={entryErrorCode}
              isSubmitting={isSubmitting}
              onSubmit={handleEntrySubmit}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </PageContainer>
  )
}

function PhoneChangeOtp({
  pendingPhone,
  onSuccess,
}: {
  pendingPhone: string
  onSuccess: () => void
}) {
  return (
    <OtpPanel
      onSuccess={onSuccess}
      sendCode={async () => {
        const { data, error } = await authClient.phoneNumber.sendOtp({
          phoneNumber: pendingPhone,
        })
        if (error) throw error
        if (!data) throw new Error('Missing data')
      }}
      verifyCode={async (code) => {
        const { error } = await authClient.phoneNumber.verify({
          phoneNumber: pendingPhone,
          code,
          updatePhoneNumber: true,
        })
        if (error) throw error
      }}
      blurb={
        <p className="text-body-md text-foreground">
          We&apos;ve sent a 6-digit code to <span className="font-semibold">{pendingPhone}</span>{' '}
          via SMS. Enter it below to confirm your new number.
        </p>
      }
    />
  )
}
